// Selector de Orden de Trabajo para asociar a un gasto contable / traslado.
//
// Estrategia de carga:
//   1. Al abrir el menu (o recibir foco), muestra las ultimas 50 OT precargadas
//      via React Query (key ['otsParaSelector', '', 0]).
//   2. Cuando el usuario digita >= 2 caracteres, loadOptions llama a filtroOTTexto
//      en el backend y muestra los resultados en el dropdown.
//   3. El boton lupa fuerza un fetch fresco (invalida cache RQ), guarda los
//      resultados como nuevas defaultOptions y reabre el dropdown con ellos.
//      Al tipear de nuevo, se vuelve al comportamiento normal de loadOptions.
//   4. Para registrar OTs nuevas en la cache: queryClient.invalidateQueries({ queryKey: ['otsParaSelector'] })

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import AsyncSelect from 'react-select/async';
import { useQueryClient } from '@tanstack/react-query';
import { OT_PARA_SELECTOR_KEY, useOTsParaSelector } from '../../hooks/useOTsParaSelector';
import { filtroOTTexto } from '../../api/common';

const selectStyles = {
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({ ...base, minHeight: 38 }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    padding: '6px 10px',
    backgroundColor: state.isFocused ? '#e7f1ff' : state.isSelected ? '#cfe2ff' : 'white',
    color: '#212529'
  })
};

const otToOption = (ot) => ({
  value: ot._id,
  label: `${ot.numeroOrden || 'OT'} — ${ot.cliente?.nombre || 'Sin cliente'}${ot.cliente?.ciudad ? ` · ${ot.cliente.ciudad}` : ''}`,
  raw: ot
});

/**
 * @param {Object} props
 * @param {Object|null} props.value     OT actualmente seleccionada (objeto raw, no solo id)
 * @param {Function}    props.onChange  (otRaw|null) => void; recibe el documento OT completo
 * @param {boolean}     props.required
 * @param {boolean}     props.disabled
 * @param {string}      props.placeholder
 */
const SelectorOT = ({
  value = null,
  onChange,
  required = false,
  disabled = false,
  placeholder = 'Buscar OT por consecutivo, cliente o ciudad...'
}) => {
  const queryClient = useQueryClient();
  const selectRef      = useRef(null);
  const shouldFocusRef = useRef(false);

  const { data: ultimasOTs = [] } = useOTsParaSelector({});

  const [inputValue,   setInputValue]   = useState('');
  const [buscando,     setBuscando]     = useState(false);
  // null = mostrar precargadas; Option[] = resultados de busqueda manual (reemplazan las precargadas)
  const [resultadosBusqueda, setResultadosBusqueda] = useState(null);
  // Incrementar para forzar re-mount de AsyncSelect tras busqueda manual
  const [selectKey,    setSelectKey]    = useState(0);

  const precargadas = useMemo(() => ultimasOTs.map(otToOption), [ultimasOTs]);

  // Al usar el boton lupa: mostrar SOLO esos resultados en el dropdown (no mezclar con precargadas).
  // Al no haber busqueda manual: mostrar las 50 OT precargadas.
  const defaultOptions = resultadosBusqueda !== null ? resultadosBusqueda : precargadas;

  // Despues de re-montar (por cambio de selectKey), enfocar para que openMenuOnFocus abra el dropdown
  useEffect(() => {
    if (shouldFocusRef.current && selectRef.current) {
      selectRef.current.focus();
      shouldFocusRef.current = false;
    }
  }, [selectKey]);

  const buscarOTs = async (searchText) => {
    const t = (searchText || '').trim();
    if (!t || t.length < 2) return precargadas;
    try {
      const data = await queryClient.fetchQuery({
        queryKey: OT_PARA_SELECTOR_KEY(t, 0),
        queryFn: async () => {
          const resp = await filtroOTTexto({
            searchText: t,
            limit: 50,
            offset: 0,
            includeAllStatuses: true
          });
          return resp?.data?.data || [];
        }
      });
      return (data || []).map(otToOption);
    } catch (err) {
      console.error('Error buscando OTs:', err);
      return [];
    }
  };

  const handleBuscarManual = async () => {
    const t = inputValue.trim();
    if (!t || t.length < 2) return;
    setBuscando(true);
    try {
      // Fetch fresco ignorando cache RQ
      queryClient.removeQueries({ queryKey: OT_PARA_SELECTOR_KEY(t, 0) });
      const results = await buscarOTs(t);
      // Guardar como defaultOptions: al re-montar el select los vera de inmediato
      setResultadosBusqueda(results);
      // Re-montar AsyncSelect para que tome los nuevos defaultOptions;
      // useEffect llama focus() → openMenuOnFocus abre el dropdown con esos resultados
      shouldFocusRef.current = true;
      setSelectKey(prev => prev + 1);
    } finally {
      setBuscando(false);
    }
  };

  const selected = value ? otToOption(value) : null;

  return (
    <div className="d-flex align-items-center gap-1">
      <div style={{ flex: 1 }}>
        <AsyncSelect
          key={selectKey}
          ref={selectRef}
          openMenuOnFocus
          defaultOptions={defaultOptions}
          loadOptions={buscarOTs}
          value={selected}
          inputValue={inputValue}
          onInputChange={(v, { action }) => {
            if (action === 'input-change') {
              setInputValue(v);
              // Al tipear de nuevo: volver al modo loadOptions normal, sin resultados forzados
              if (resultadosBusqueda !== null) setResultadosBusqueda(null);
            }
            if (action === 'set-value') setInputValue('');
          }}
          onChange={(opt) => {
            onChange && onChange(opt ? opt.raw : null);
            setInputValue('');
            setResultadosBusqueda(null);
          }}
          isClearable
          isDisabled={disabled}
          placeholder={placeholder}
          noOptionsMessage={({ inputValue: iv }) => {
            if (iv && iv.length >= 2) return 'Sin resultados en la BD. Intenta con otro texto.';
            if (resultadosBusqueda !== null && resultadosBusqueda.length === 0)
              return 'Sin resultados — intenta con otro texto.';
            return precargadas.length === 0 ? 'Cargando OTs...' : 'Escribe para buscar';
          }}
          loadingMessage={() => 'Buscando OTs...'}
          styles={selectStyles}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition="fixed"
          aria-required={required}
        />
      </div>
      <Button
        variant="outline-secondary"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleBuscarManual}
        disabled={buscando || disabled || !inputValue.trim() || inputValue.length < 2}
        title="Buscar en base de datos"
        style={{ flexShrink: 0, height: 38 }}
      >
        {buscando ? <Spinner size="sm" animation="border" /> : <FaSearch />}
      </Button>
    </div>
  );
};

export default SelectorOT;
